import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 941 Filing Instructions - Quarterly Employment Tax Form"
          description="Discover comprehensive Form 941 instructions. Learn how to accurately complete and file Form 941 for your payroll taxes. Get guidance and helpful tips."
          keywords="Form 941, Payroll tax filing, IRS Form 941, Employment tax, Quarterly tax reporting"
          canonical="resources/employment-tax/form-941-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 941</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your IRS Form 941 for the 2026 tax year with these easy
          instructions.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions`}
          >
            E-File 941 Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        Form 941 (Employer's Quarterly Federal Tax Return) is
                        filed by employers with the IRS using their{' '}
                        <b>Employer Identification Number (EIN).</b> It reports
                        employee wages, <b>federal income tax withheld,</b> and
                        both the employer and employee portions of{' '}
                        <b>Social Security and Medicare</b> (
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/fica-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          FICA
                        </Link>
                        ) taxes.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                          sx={{ color: '#0202ff' }}
                        >
                          Due Date:
                        </Link>{' '}
                        The form is due quarterly on
                        <strong>
                          {' '}
                          April 30, July 31, October 31,
                        </strong> and <strong>January 31</strong>.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Penalties:</b> Failure to file or pay on time may
                        result in IRS penalties ranging from <b>0.5% to 25%,</b>{' '}
                        along with applicable interest on unpaid taxes.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What&apos;s New for 2026
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The IRS has introduced{' '}
                  <Link
                    href="https://blog.taxzerone.com/posts/whats-new-in-form-941-for-2026-key-changes-explained/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    key updates for Form 941
                  </Link>{' '}
                  in 2026 that mainly focus on improving payroll reporting,
                  refund processing, and clarifying filing roles.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Social Security Tax:
                    </span>{' '}
                    6.2% for both employee and employer; wage base limit is
                    $184,500.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Medicare Tax:</span>{' '}
                    1.45% for both, no wage base limit (same as 2025).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Household Workers:</span>{' '}
                    Taxes apply if you pay $3,000 or more in cash wages.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Election Workers: </span>{' '}
                    Taxes apply if paid $2,500 or more in cash or an equivalent.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Aggregate Return Filers Only:{' '}
                    </span>{' '}
                    Indicate your filing role, such as:
                  </li>
                  <ul>
                    <li>Section 3504 agents </li>
                    <li>
                      Certified Professional Employer Organizations (CPEOs){' '}
                    </li>
                    <li>Other authorized third-party filers </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Direct Deposit Option for Refunds:{' '}
                    </span>{' '}
                    Line 15 now includes additional fields that allow employers
                    to enter their bank details and receive refunds through
                    direct deposit.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Electronic Payments Required:{' '}
                    </span>{' '}
                    <Link
                      href="https://www.irs.gov/eo14247"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      EO 14247
                    </Link>{' '}
                    also mandates electronic payment of any balance due on Form
                    941.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Electronic Return Transcripts:{' '}
                    </span>{' '}
                    Form 941{' '}
                    <Link
                      href="https://www.irs.gov/businesstranscript"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      transcripts
                    </Link>{' '}
                    for tax years 2023 and later are now available through your{' '}
                    <Link
                      href="https://www.irs.gov/BusinessAccount"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      IRS Business Tax Account
                    </Link>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Withholding on Qualified Tips:
                    </span>{' '}
                    For tax years 2025 through 2028, the{' '}
                    <span style={{ fontWeight: 600 }}>
                      One Big Beautiful Bill Act (P.L. 119-21)
                    </span>{' '}
                    allows employees and self-employed individuals to{' '}
                    <span style={{ fontWeight: 600 }}>
                      deduct up to $25,000 in qualified tips
                    </span>{' '}
                    on their federal income tax returns. Qualified tips include:
                  </li>
                  <ul>
                    <li>Cash tips received directly from customers </li>
                    <li>Credit-card or charged tips </li>
                    <li>Tips received through tip-sharing arrangements </li>
                  </ul>
                  <li style={{ listStyleType: 'none' }}>
                    If an employee wants to adjust their withholding to account
                    for this deduction, they can submit an{' '}
                    <span style={{ fontWeight: 600 }}>
                      updated Form W-4 (Employee’s Withholding Certificate).
                    </span>{' '}
                  </li>
                  <li style={{ listStyleType: 'none' }}>
                    Employers should then use the federal income tax withholding
                    methods in{' '}
                    <span style={{ fontWeight: 600 }}>Publication 15-T</span> to
                    adjust the employee’s withholding. Even with this deduction,
                    tips are{' '}
                    <span style={{ fontWeight: 600 }}>
                      still subject to Social Security and Medicare taxes.
                    </span>{' '}
                    These taxes apply when an employee receives{' '}
                    <span style={{ fontWeight: 600 }}>
                      $20 or more in tips in a month.
                    </span>
                  </li>
                  <li style={{ listStyleType: 'none' }}>
                    <span style={{ fontWeight: 600 }}>
                      Reporting Requirements:
                    </span>{' '}
                    Employers and other payers must report tip income on
                    information returns such as:
                  </li>
                  <ul>
                    <li>
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form W-2
                      </Link>{' '}
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-misc-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-MISC
                      </Link>{' '}
                    </li>
                    <li>
                      <Link
                        href="/resources/information-returns/form-1099-nec-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-NEC{' '}
                      </Link>{' '}
                    </li>
                  </ul>
                  <li style={{ listStyleType: 'none' }}>
                    These forms must also include the cash tips received and the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Treasury Tipped Occupation Code(s) for the worker.
                    </span>{' '}
                    The IRS has provided temporary transition relief for the
                    2025 reporting requirements. More details can be found in
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Notice 2025-62,
                    </span>{' '}
                    <Link
                      href="https://www.irs.gov/irb/2025-48_IRB#NOT-2025-62"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      2025-48 I.R.B. 740
                    </Link>
                    .
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Qualified Overtime Compensation:
                    </span>{' '}
                    Employees who earn overtime pay may claim a deduction for
                    qualified overtime compensation. The maximum deduction is:
                  </li>
                  <ul>
                    <li>$12,500 for single filers </li>
                    <li>$25,000 for married taxpayers filing jointly </li>
                  </ul>
                  <li style={{ listStyleType: 'none' }}>
                    Only the overtime premium portion qualifies for the
                    deduction under section 7 of the{' '}
                    <span style={{ fontWeight: 600 }}>
                      Fair Labor Standards Act (FLSA)
                    </span>{' '}
                    of 1938. For example, if overtime is paid at 1.5 times the
                    regular rate, only the extra 0.5 portion (the premium) is
                    considered qualified overtime compensation.{' '}
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="employ-promo-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#f8f9fa',
                          fontSize: 18,
                        }}
                      >
                        E-filing Form 941 is{' '}
                        <Box component="span" sx={{ color: '#e2e00f' }}>
                          faster, safer, and IRS-recommended
                        </Box>
                        .{' '}
                      </Typography>
                      <Typography variant="body2" color="#f5efeb">
                        File with TaxZerone and receive{' '}
                        <span style={{ fontWeight: 600, color: '#fff' }}>
                          {' '}
                          instant IRS status updates
                        </span>
                        —accurate and secure.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions`}
                      >
                        File Form 941 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 941 is filed quarterly by most businesses that pay wages
                  to employees and withhold taxes such as federal income tax,
                  Social Security tax, and Medicare tax. You can{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    efile IRS Form 941 online
                  </Link>{' '}
                  for a faster and more accurate submission.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you don&apos;t have any employees during a
                  particular quarter or if you have no tax liability for a
                  quarter, you may still be required to{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    file a Form 941
                  </Link>{' '}
                  to report that no wages were paid, and no taxes were withheld.
                </Alert>
                <Box
                  sx={{
                    mb: 4,
                    background: '#e5f6fd',
                    border: '2px solid #1993d6',
                    borderRadius: 3,
                    py: 2,
                    px: 3,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { lg: 22, md: 20, sm: 20, xs: 18 },
                      fontWeight: 600,
                      mb: 1,
                      color: '#014361',
                    }}
                  >
                    Exceptions
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      mt: 0,

                      textAlign: 'left',
                      '& li': { mb: 1.2 },
                    }}
                  >
                    <li>
                      Employers who received notification from IRS to{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-944/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 944
                      </Link>{' '}
                      instead of quarterly 2026 form 941
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Seasonal employers
                      </span>{' '}
                      are not needed to{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-941/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form 941
                      </Link>{' '}
                      for the quarter if the wages are not paid.{' '}
                    </li>
                    <li>
                      Employers of{' '}
                      <span style={{ fontWeight: 600 }}>
                        household employees
                      </span>{' '}
                      should file schedule H of Form 1040 instead of 941
                      form.{' '}
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Employers of{' '}
                      <span style={{ fontWeight: 600 }}>farm employees</span>{' '}
                      should file{' '}
                      <Link
                        href="/employment-tax/e-file-irs-form-943/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 943{' '}
                      </Link>
                      instead of tax form 941 for wages paid for agricultural
                      labor.{' '}
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When are Form 941 due?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The deadline for{' '}
                  <Link
                    href="/employment-tax/e-file-irs-form-941/"
                    sx={{ color: '#0202ff' }}
                  >
                    filing Form 941
                  </Link>{' '}
                  is typically the last day of the month that follows the end of
                  the calendar quarter.
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 100,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Quarter
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Quarter Includes
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Q1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            January, February, March
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'left', fontWeight: 600 }}
                          >
                            April 30
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Q2
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            April, May, June
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'left', fontWeight: 600 }}
                          >
                            July 31
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Q3
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            July, August, September
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'left', fontWeight: 600 }}
                          >
                            October 31
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Q4
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            October, November, December
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'left', fontWeight: 600 }}
                          >
                            January 31
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Dates may change due to weekends or federal
                  holidays.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File IRS Form 941: Required Information
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 3 },
                    mb: 5,
                    '& li': { display: 'flex' },
                  }}
                >
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Business Details</b>
                      &nbsp;- Legal name, EIN, and business address.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Employee Details</b>
                      &nbsp;- Number of employees and total taxable wages paid
                      during the quarter.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Taxes</b>
                      &nbsp;-
                    </Box>
                  </li>
                  <ul style={{ marginBottom: 10 }}>
                    <li>
                      <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />
                      Federal income tax withheld from employee wages{' '}
                    </li>
                    <li>
                      <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />
                      Social Security and Medicare taxes
                    </li>
                    <li>
                      <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />
                      Taxable Social Security and Medicare wages and tips
                    </li>
                    <li>
                      <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />
                      Any Additional Medicare tax withheld (if applicable)
                    </li>
                  </ul>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Deposits </b>
                      &nbsp;- Any federal tax deposits made during the quarter
                      toward your payroll tax liability.
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Schedule B (Form 941) </b>
                      &nbsp;- Required{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        only if you&apos;re a semiweekly schedule depositor
                      </span>{' '}
                      or if you{' '}
                      <span style={{ fontWeight: 600 }}>
                        accumulate $100,000 or more in tax liability on any day
                        in the quarter.
                      </span>{' '}
                      Use this form to report daily tax liabilities.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Instructions for Form 941
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow these instructions to complete your 941 filing online
                  accurately with the IRS.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-instrction-img.webp" />
                  <source srcSet="/assets/images/f941-instrction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-instrction-img.png"
                    loading="lazy"
                    alt="Form 941: Enter the EIN, Name, Complete address and select the Quarter "
                    width={760}
                    height={292}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Employer Identification Number (EIN), legal name of
                  the employer, Business name, complete address including street
                  name, city, state, and Zip code.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On the right side of the form, check the appropriate box such
                  as Quarter 1 or Quarter 2 or Quarter 3 or Quarter 4 for the
                  quarter you are filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  For Aggregate Return Filers Only:
                </Typography>
                <Typography variant="body1">
                  Check the box that identifies your filer type (select one):
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>Section 3504 Agent</li>
                  <li>Certified Professional Employer Organization (CPEO) </li>
                  <li>Other Third-Party Filer </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part 1: Quarter Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-quarter-img.webp" />
                  <source srcSet="/assets/images/f941-quarter-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-quarter-img.png"
                    loading="lazy"
                    alt="Form 941- Line 1: Employee count who received wages, tips, or other compensation"
                    width={747}
                    height={41}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the number of employees who received wages, tips or
                  other compensation during a specific pay period - March 12
                  (Quarter 1), June 12 (Quarter 2), September 12 (Quarter 3), or
                  December 12 (Quarter 4).
                </Typography>
                <Typography variant="body1">
                  Don&apos;t include these employees:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li> Household employees </li>
                  <li>Employees in non-pay status for the pay period </li>
                  <li>Farm employees</li>
                  <li>Pensioners</li>
                  <li>Active members of the U.S. Armed Forces. </li>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips:</b> Employers in American Samoa, Guam, the
                  Commonwealth of the Northern Mariana Islands (CNMI), the U.S.
                  Virgin Islands (USVI), and Puerto Rico can skip lines 2 and 3,
                  except when they have employees subject to U.S. income tax
                  withholding.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f941-wages-img.webp" />
                  <source srcSet="/assets/images/f941-wages-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-wages-img.png"
                    loading="lazy"
                    alt="Form 941-Line 2: Enter the wages, tips, and other compensation"
                    width={745}
                    height={26}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the wages, tips and other compensation paid during the
                  quarter. This includes
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Sick pay paid by your agent </li>
                  <li>
                    Sick pay paid by third party that is not your agent (e.g.:
                    insurance company) if you were notified on time that the
                    payments were made{' '}
                  </li>
                  <li>
                    The third party has transferred the liability for paying the
                    employer’s taxes (like Social Security and Medicare) to
                    you.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Do not report if you paid sick pay as a third
                  party (not an employer agent) as long as you gave the
                  policyholder timely notice about those payments.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f941-federal-income-img.webp" />
                  <source srcSet="/assets/images/f941-federal-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-federal-income-img.png"
                    loading="lazy"
                    alt="Form 941-Line 3: Enter the Federal income tax withheld from wages and tips"
                    width={745}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the federal income tax withheld from employees on this
                  quarter&apos;s wages, tips, and other compensation benefits.
                  Enter the federal income tax withheld or required to withhold
                  by a third-party payer of sick pay.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-medicare-tax-img.webp" />
                  <source srcSet="/assets/images/f941-medicare-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-medicare-tax-img.png"
                    loading="lazy"
                    alt="Form 941-Line 4: If no wages are subject to FICA, check the box"
                    width={748}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box on line 4, if wages, tips, and other
                  compensation are not subject to Social Security or Medicare
                  tax and proceed with line 6. If this question does not apply
                  to you, leave the box blank.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-taxable-img.webp" />
                  <source srcSet="/assets/images/f941-taxable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-taxable-img.png"
                    loading="lazy"
                    alt="Form 941 Line 5a to 5d: Enter the FICA wages and tips on column 1."
                    width={580}
                    height={142}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5a: Taxable Social Security Wages
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    In Column 1, enter the total amount of employee wages that
                    are subject to Social Security tax and multiply that number
                    by 0.124 to get the tax. Enter the result in Column 2.{' '}
                  </li>
                  <li>
                    Employers should stop collecting social security tax once an
                    employee attains a taxable wage and tips of $176,100. Income
                    and Medicare taxes must still be withheld on all wages and
                    tips.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5b: Taxable Social Security Tips
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In column 1, enter the total tips your employees reported that
                  are subject to Social Security tax and multiply that number by
                  0.124. Enter the result in Column 2.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5c: Taxable Medicare Wages and Tips
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In column 1, enter the total wages, tips, sick pay, and
                  taxable fringe benefits subject to Medicare tax and multiply
                  the total by 0.029. Enter the result in Column 2.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5d: Wages & Tips subject to Additional Medicare Tax
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter all wages, tips, sick pay, and taxable fringe benefits
                    that are subject to Additional Medicare Tax withholding.
                    Employers are required to withhold additional Medicare tax
                    for employees who earned more than $200,000.
                  </li>
                  <li>
                    In column 1, enter the amount over $200,000 that is subject
                    to the Additional Medicare Tax and multiply by 0.009 (0.9%).
                    Enter the result in Column 2.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f941-total-social-img.webp" />
                  <source srcSet="/assets/images/f941-total-social-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-total-social-img.png"
                    loading="lazy"
                    alt="Form 941-Line 5e: Enter the total FICA taxes."
                    width={747}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5e: Total Social Security and Medicare Taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add all amounts in Column 2 from lines 5a through 5d and enter
                  the result on line 5e.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-notice-tax-img.webp" />
                  <source srcSet="/assets/images/f941-notice-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-notice-tax-img.png"
                    loading="lazy"
                    alt="Form 941-Line 5f: Section 3121(q) Notice and Demand"
                    width={745}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5f: Section 3121(q) Notice and Demand - Tax Due on
                  Unreported Tips
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the amount of tax due from unreported tips on line 5f.
                  </li>
                  <li>
                    Section 3121(q) Notice and Demand is issued by the IRS to
                    inform an employer about the amount of tips that employees
                    have either failed to report or have underreported.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f941-total-tax-img.webp" />
                  <source srcSet="/assets/images/f941-total-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-total-tax-img.png"
                    loading="lazy"
                    alt="Form 941-Line 6: Total Taxes before adjustment"
                    width={750}
                    height={24}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6: Total Taxes Before Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To calculate the total taxes before adjustments, add the
                  amount entered in{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Total federal income tax withheld from wages, tips, and
                    other compensation (line 3)
                  </li>
                  <li>Total social security and Medicare taxes (line 5e)</li>
                  <li>
                    Tax due under Section 3121(q) Notice and Demand (line
                    5f){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the result on line 6.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-current-quarter-img.webp" />
                  <source srcSet="/assets/images/f941-current-quarter-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-current-quarter-img.png"
                    loading="lazy"
                    alt="Form 941-Line 7: Enter the current quarter’s adjustment for fraction of cents"
                    width={758}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7: Current quarter&apos;s adjustment for fractions of
                  cents.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any adjustments for fractions of cents (due to rounding)
                  that occur when calculating Social Security and Medicare taxes
                  in column 2 of lines 5a–5d.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-sick-pay-img.webp" />
                  <source srcSet="/assets/images/f941-sick-pay-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-sick-pay-img.png"
                    loading="lazy"
                    alt="Form 941-Line 8: Enter the current quarter’s adjustment for sick pay"
                    width={752}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8: Current quarter&apos;s adjustment for sick pay
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your third-party payer of sick pay (not an agent) has
                    withheld and deposited the employee share of social security
                    and Medicare taxes and transfers the employer tax share to
                    you, enter a negative adjustment on line 8.
                  </li>
                  <li>
                    If you are the third-party sick pay payer and transfers
                    employers tax share to the employer, then enter a negative
                    adjustment on line 8{' '}
                  </li>
                  <li>
                    No need to adjust on line 8 if the sick pay is paid by third
                    party (employers&apos; agent){' '}
                  </li>
                  <li>
                    Include the sick pay on line 5a, line 5c, and line 5d if the
                    withholding threshold is met.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f941-adjustment-img.webp" />
                  <source srcSet="/assets/images/f941-adjustment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-adjustment-img.png"
                    loading="lazy"
                    alt="Form 941-Line 9: current quarter’s adjustment for tips and group-term life insurance "
                    width={749}
                    height={22}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9: Current quarter&apos;s adjustments for tips and
                  group-term life insurance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter a negative adjustment for any uncollected employee share
                  of social security and Medicare taxes on tips and group-term
                  life insurance premiums paid for former employees.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-combines-line-img.webp" />
                  <source srcSet="/assets/images/f941-combines-line-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-combines-line-img.png"
                    loading="lazy"
                    alt="Form 941-Line 10: Enter the total taxes after adjustments"
                    width={754}
                    height={22}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10: Total Taxes After Adjustments
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts on line 6 through 9 and enter the result.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-small-business-img.webp" />
                  <source srcSet="/assets/images/f941-small-business-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-small-business-img.png"
                    loading="lazy"
                    alt="Form 941-Line 11: Qualified small business payroll tax credit "
                    width={750}
                    height={23}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11: Qualified Small Business Payroll Tax Credit for
                  Increasing Research Activities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of the credit from Form 8974, line 12. If
                  applicable enter line 17. Attach Form 8974 to this return.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you enter an amount on line 11, you must
                  attach Form 8974.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f941-nonrefunable-credit-img.webp" />
                  <source srcSet="/assets/images/f941-nonrefunable-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-nonrefunable-credit-img.png"
                    loading="lazy"
                    alt="Form 941-Line 12: Total taxes after adjustments and nonrefundable credits. "
                    width={756}
                    height={24}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 12: Total Taxes After Adjustments and Nonrefundable
                  Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 11 from line 10 and enter the result on line 12
                  which cannot be less than zero.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  1. If Line 12 or last quarter’s Line 12 is less than $2,500,
                  and you didn’t have a $100,000 next-day deposit obligation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can pay the amount with Form 941, or you may deposit
                  separately. You must pay on time to avoid penalties.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  2. If line 12 or last quarter’s Line 12 is $2,500 or more and
                  you incurred a $100,000 next-day deposit obligation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must deposit your taxes according to your deposit schedule
                  (monthly or semiweekly). The amount on Line 12 must match the
                  “Total liability for the quarter” on Line 16 or Schedule B
                  (Form 941).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Semiweekly schedule depositor must complete
                  Schedule B (Form 941). If failed, the IRS may impose deposit
                  penalties.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f941-total-deposit-img.webp" />
                  <source srcSet="/assets/images/f941-total-deposit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-total-deposit-img.png"
                    loading="lazy"
                    alt="Form 941-Line 13: Enter the total deposits for this quarter "
                    width={758}
                    height={34}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 13: Total Deposits for This Quarter
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter total deposits you made for this quarter, including any
                  overpayment from a previous quarter that you decided to apply
                  to this return (instead of getting a refund). Also include any
                  overpayment that you applied for from{' '}
                  <Link
                    href="/employment-tax/efile-irs-form-941-x-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 941-X
                  </Link>
                  , 941-X (PR), or 944-X in the current quarter.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-balance-due-img.webp" />
                  <source srcSet="/assets/images/f941-balance-due-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-balance-due-img.png"
                    loading="lazy"
                    alt="Form 941-Line 14: Enter the Balance due "
                    width={903}
                    height={28}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 14: Balance Due
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The amount entered on line 12 is more than line 13, enter the
                  difference on line 14.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-overpayment-img.webp" />
                  <source srcSet="/assets/images/f941-overpayment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-overpayment-img.png"
                    loading="lazy"
                    alt="Form 941-Line 15: Enter the Overpayment difference "
                    width={760}
                    height={76}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15a:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 13 is greater than Line 12, it means you have overpaid
                  your taxes for the quarter.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15b:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Choose one of the following options:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Apply to next return –
                    </span>{' '}
                    Apply the overpayment as a credit toward your next
                    filing.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Send a refund –</span>{' '}
                    Receive the overpaid amount back from the IRS.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15c:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Routing number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15d:
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Choose your account type:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Checking </li>
                  <li>Savings </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 15e:
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter your account number
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part 2: Deposit Schedule and Tax Liability for this Quarter
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 16:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-check-one-img.webp" />
                  <source srcSet="/assets/images/f941-check-one-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-check-one-img.png"
                    loading="lazy"
                    alt="Form 941-Line 16: If Line 12 on this return or prior quarter < $2,500 and didn’t incur $100,000 obligation. "
                    width={733}
                    height={71}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For this current quarter or previous quarter, Line 12 is less
                  than $2,500 and you didn’t owe $100,000 next-day deposit
                  obligation during the quarter. check the first box on line 16
                  and go to Part 3.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-tax-liability-img.webp" />
                  <source srcSet="/assets/images/f941-tax-liability-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-tax-liability-img.png"
                    loading="lazy"
                    alt="Form 941: Monthly Schedule Depositor – enter the tax liability"
                    width={741}
                    height={182}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the second box on line 16, if you are a monthly schedule
                  depositor. Enter the tax liability for each month. Add the
                  amounts for each month and enter the result in the “Total
                  liability for quarter” box which should be equal to amount
                  entered on line 12.
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips:</b> You must enter the actual tax liability for each
                  month, not the tax deposits you made during the month.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f941-schedule-img.webp" />
                  <source srcSet="/assets/images/f941-schedule-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-schedule-img.png"
                    loading="lazy"
                    alt="Semiweekly schedule depositor should complete Schedule B of Form 941"
                    width={731}
                    height={45}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check the third box on line 16, if you are a semiweekly
                  depositor for any quarter. Submit Schedule B with your form
                  941 2026 and go to part 3.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part 3: About Business
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-business-close-img.webp" />
                  <source srcSet="/assets/images/f941-business-close-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-business-close-img.png"
                    loading="lazy"
                    alt="Form 941 Line 17: If you have closed your business, enter the final date you paid wages"
                    width={739}
                    height={59}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the box on line 17 if your business has closed or
                  stopped paying wages. Enter the last date wages were paid.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Additionally, attach a Final Payroll Statement that includes
                  all relevant payroll details, such as the name of the person
                  responsible for maintaining payroll records and the address
                  where the records will be stored. This statement is crucial
                  for ensuring proper documentation of your final payroll.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  During e-filing, additional details will be collected to
                  ensure compliance and accurate processing of your return.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-seasonal-employ-img.webp" />
                  <source srcSet="/assets/images/f941-seasonal-employ-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-seasonal-employ-img.png"
                    loading="lazy"
                    alt="Form 941 Line 18: Check the box if you are a Seasonal employer"
                    width={707}
                    height={32}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Check the box on line 18, if you are an employer who hires
                  employees seasonally and doesn’t file every quarter with the
                  IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part 4: Third-Party Designee
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f941-employee-img.webp" />
                  <source srcSet="/assets/images/f941-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f941-employee-img.png"
                    loading="lazy"
                    alt="Enter Designee name, Phone number, and 5-digit PIN"
                    width={744}
                    height={119}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Select the “Yes” box if an employer allows an employee or
                    tax preparer to discuss IRS Form 941 return with the IRS.
                    You must provide the designee’s name, phone number, and a
                    5-digit personal identification number (PIN) for security
                    when talking to the IRS.
                  </li>
                  <li>
                    Select the “No” box, if only you can discuss the return with
                    the IRS.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part 5: Signature and Authorization
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choose one of the following signature options:{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                    ml: { xs: 0, sm: 3 },
                    '& li': { display: 'flex' },
                  }}
                >
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>Form 8453-EMP Signature Document</b>
                      <br />
                      Enter the signatory details and e-sign on Form 8453-EMP
                    </Box>
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                      textAlign: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    (OR)
                  </li>
                  <li
                    style={{
                      marginBottom: 10,
                    }}
                  >
                    <DoneIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <b>94x Online Signature PIN </b>
                      <br />
                      Enter the name, title of signer and 10-digit signature PIN
                      provided by the IRS to authenticate you as an authorized
                      signer.
                    </Box>
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontSize: 24, marginRight: 8 }}>ðŸ“</span>{' '}
                  <Typography>
                    If you have not received your 94x PIN, TaxZerone offers the{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      style={{ fontWeight: 600, color: '#0202ff' }}
                    >
                      Form 94x Online Signature PIN
                    </Link>{' '}
                    request for <span style={{ fontWeight: 600 }}>FREE</span>.
                  </Typography>
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone supports easily allowing e-signatures, ensuring a
                  seamless process for your filing.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website.
                        <br style={{ marginBottom: 8 }} />
                        For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions
                        </Link>{' '}
                        for Form 941 and{' '}
                        <Link
                          href="https://www.irs.gov/publications/p15"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Employer’s Tax Guide
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 941 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              <Link
                href="/employment-tax/e-file-irs-form-941/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                E-File IRS Form 941
              </Link>{' '}
              easily with TaxZerone starting at just <b>$6.99/return</b>.You
              will only have to enter the required information,
              <br /> pay and sign Form 941, and transmit the return to the IRS.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              The filing process gets completed in less than 5 minutes! <br />{' '}
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941instructions`}
              >
                E-file Form 941 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941Instructions;
